/**
 * Step one of signing in: the address a code is sent to.
 *
 * The heading names what you are signing in *for* — the event being joined —
 * because this screen sits between a room code and a lobby, and a bare "sign
 * in" at that moment reads as an obstacle rather than as part of joining.
 */

import { useState, type FormEvent } from "react";
import { BrandLockup } from "@tq/ui";
import { AuthError, sendCode } from "./authApi.js";

export interface SignInScreenProps {
  heading: string;
  subheading?: string;
  /** Called with the address once a code is on its way. */
  onSent: (email: string) => void;
}

export function SignInScreen({
  heading,
  subheading,
  onSent,
}: SignInScreenProps): React.ReactElement {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (address.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      await sendCode(address);
      onSent(address);
    } catch (cause) {
      setError(
        cause instanceof AuthError
          ? cause.message
          : "We could not send a code just now. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tq-player">
      {/* The card the join screen wears. A facilitator opens this on a laptop,
          and `.tq-player-main` is a phone layout with no width cap — so past
          640px the label sat at one edge, the field spanned the whole monitor,
          and there was a foot of nothing between them. The join screen solved
          this when it grew the card; these two auth screens were missed. Same
          wrapper, same breakpoint, same 460px, rather than a second answer. */}
      <form className="tq-player-main tq-phone-card" onSubmit={submit}>
        {/* The lockup rather than the 🏛️ this screen used to lead with — the
            same swap the join screen made, and for the same reason: it is the
            mark every other surface carries, and an emoji standing in for a
            brand is a placeholder that stopped looking like one. The heading
            below still says which screen this is. */}
        <div className="tq-phone-join-title">
          <BrandLockup brand={{ top: "FPPTA", main: "TRUSTEE", accent: "SHOWDOWN" }} />
          <span className="tq-phone-join-sub">{heading}</span>
        </div>

        {subheading ? <p className="tq-phone-hint">{subheading}</p> : null}

        <label className="tq-phone-label" htmlFor="tq-signin-email">
          YOUR EMAIL
        </label>
        <input
          id="tq-signin-email"
          className="tq-phone-code-input"
          style={{ fontSize: 18, letterSpacing: "normal", textTransform: "none" }}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="you@example.com"
          value={email}
          disabled={busy}
          required
          onChange={(event) => setEmail(event.target.value)}
        />

        <div className="tq-phone-hint" role="alert">
          {error}
        </div>

        <button
          type="submit"
          className="tq-phone-primary"
          disabled={busy || email.trim().length === 0}
          style={{ minHeight: 56 }}
        >
          {busy ? "Sending…" : "Send me a code"}
        </button>

        <p className="tq-phone-hint">
          We&apos;ll email you a six-digit code. No password to remember.
        </p>
      </form>
    </div>
  );
}
